import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { PlayerSpawnElasticsearchHit } from '../types/playerSpawn';

export interface PlayerSpawnFilters {
  itemIdSearch: string;
  itemIdInclude: string[];
  itemIdExclude: string[];
  playerUuidSearch: string[];
  playerUuidExclude: string[];
  locationSearch: string[];
  logTypeSearch: string[];
  dateRange: {
    from: Date;
    to: Date;
  };
}

export interface PlayerSpawnsTabState {
  id: string;
  name: string;
  filters: PlayerSpawnFilters;
  hits: PlayerSpawnElasticsearchHit[];
  totalHits: number;
  aggregations: {
    locations: string[];
    logTypes: string[];
    itemIds: string[];
  };
  highlightedUsers: { [uuid: string]: string };
  isLoading: boolean;
}

interface PlayerSpawnsTabsContextType {
  tabs: PlayerSpawnsTabState[];
  activeTabId: string | null;
  addTab: (filters?: Partial<PlayerSpawnFilters>, name?: string) => void;
  removeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateFilters: (tabId: string, newFilters: Partial<PlayerSpawnFilters>) => void;
  updateTabState: (tabId: string, newState: Partial<Omit<PlayerSpawnsTabState, 'id' | 'filters'>>) => void;
  getActiveTab: () => PlayerSpawnsTabState | undefined;
  updateTabName: (id: string, name: string) => void;
  toggleUserHighlight: (tabId: string, uuid: string) => void;
}

const PlayerSpawnsTabsContext = createContext<PlayerSpawnsTabsContextType | undefined>(undefined);

export const usePlayerSpawnsTabs = () => {
  const context = useContext(PlayerSpawnsTabsContext);
  if (!context) {
    throw new Error('usePlayerSpawnsTabs must be used within a PlayerSpawnsTabsProvider');
  }
  return context;
};

const getDefaultFilters = (): PlayerSpawnFilters => ({
  itemIdSearch: '',
  itemIdInclude: [],
  itemIdExclude: [],
  playerUuidSearch: [],
  playerUuidExclude: [],
  locationSearch: [],
  logTypeSearch: [],
  dateRange: {
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  },
});

export const PlayerSpawnsTabsProvider = ({ children }: { children: ReactNode }) => {
  const [tabs, setTabs] = useState<PlayerSpawnsTabState[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const addTab = useCallback((filters?: Partial<PlayerSpawnFilters>, name = 'New Tab') => {
    const newTab: PlayerSpawnsTabState = {
      id: uuidv4(),
      name,
      filters: { ...getDefaultFilters(), ...filters },
      hits: [],
      totalHits: 0,
      aggregations: { locations: [], logTypes: [], itemIds: [] },
      highlightedUsers: {},
      isLoading: true,
    };
    setTabs(prevTabs => [...prevTabs, newTab]);
    setActiveTabId(newTab.id);
  }, []);

  const removeTab = (id: string) => {
    const newTabs = tabs.filter(tab => tab.id !== id);
    setTabs(newTabs);
    if (activeTabId === id) {
      setActiveTabId(newTabs.length > 0 ? newTabs[newTabs.length - 1].id : null);
    }
  };

  const setActiveTab = (id: string) => {
    setActiveTabId(id);
  };

  const updateFilters = useCallback((tabId: string, newFilters: Partial<PlayerSpawnFilters>) => {
    setTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.id === tabId ? { ...tab, filters: { ...tab.filters, ...newFilters } } : tab
      )
    );
  }, []);

  const updateTabState = useCallback((tabId: string, newState: Partial<Omit<PlayerSpawnsTabState, 'id' | 'filters'>>) => {
    setTabs(prevTabs =>
      prevTabs.map(tab => (tab.id === tabId ? { ...tab, ...newState } : tab))
    );
  }, []);

  const getActiveTab = () => tabs.find(tab => tab.id === activeTabId);

  const updateTabName = (id: string, name: string) => {
    setTabs(tabs.map(tab => (tab.id === id ? { ...tab, name } : tab)));
  };

  const HIGHLIGHT_COLORS = [
    'rgba(255, 255, 0, 0.1)',
    'rgba(0, 255, 255, 0.1)',
    'rgba(255, 0, 255, 0.1)',
    'rgba(0, 255, 0, 0.1)',
    'rgba(255, 165, 0, 0.1)',
    'rgba(255, 192, 203, 0.1)',
    'rgba(75, 0, 130, 0.1)',
    'rgba(238, 130, 238, 0.1)',
    'rgba(255, 0, 0, 0.1)',
    'rgba(0, 0, 255, 0.1)',
  ];

  const toggleUserHighlight = (tabId: string, uuid: string) => {
    setTabs(tabs.map(tab => {
      if (tab.id === tabId) {
        const newHighlights = { ...tab.highlightedUsers };
        if (newHighlights[uuid]) {
          delete newHighlights[uuid];
        } else {
          const usedColors = Object.values(newHighlights);
          const nextColor = HIGHLIGHT_COLORS.find(c => !usedColors.includes(c)) || HIGHLIGHT_COLORS[0];
          newHighlights[uuid] = nextColor;
        }
        return { ...tab, highlightedUsers: newHighlights };
      }
      return tab;
    }));
  };

  const value = {
    tabs,
    activeTabId,
    addTab,
    removeTab,
    setActiveTab,
    updateFilters,
    updateTabState,
    getActiveTab,
    updateTabName,
    toggleUserHighlight,
  };

  return (
    <PlayerSpawnsTabsContext.Provider value={value}>
      {children}
    </PlayerSpawnsTabsContext.Provider>
  );
};
